import { cloneElement, isValidElement, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

/**
 * Label + hint/error chrome for a form control, associated via `useId` so
 * the control is properly labelled for assistive tech. `children` must be
 * a single element accepting an `id` prop (e.g. `TextInput`, a native
 * `<input>`, or `DateField`'s inner input).
 */
export function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  const generatedId = useId();
  const control =
    isValidElement<{ id?: string }>(children) && children.props.id
      ? children
      : isValidElement<{ id?: string }>(children)
        ? cloneElement(children, { id: generatedId })
        : children;
  const controlId =
    isValidElement<{ id?: string }>(control) && control.props.id ? control.props.id : generatedId;

  return (
    <div className={className}>
      <label htmlFor={controlId} className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {control}
      {error ? (
        <div className="text-xs text-red-600">{error}</div>
      ) : hint ? (
        <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      ) : null}
    </div>
  );
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  mono?: boolean;
};

/**
 * Shared text input control — pair with `Field` for label/hint/error
 * chrome. `mono` switches to `.font-num` for serial/price-style values.
 */
export function TextInput({ mono, className, ...props }: TextInputProps) {
  return (
    <input
      className={cn(
        'w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-muted disabled:text-muted-foreground',
        mono && 'font-num',
        className,
      )}
      {...props}
    />
  );
}
